import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, AlertTriangle, Calendar, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../utils/api';

// Alert Badge Component (for navbar)
export const AlertBadge = ({ count }) => {
  if (!count || count === 0) return null;
  
  return (
    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
      {count > 9 ? '9+' : count}
    </span>
  );
};

// Alert Icon with Badge (for navbar)
export const AlertIcon = ({ onClick }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await api.getAlertsCount();
        setCount(data.unread || 0);
      } catch (e) {
        // Silent fail
      }
    };
    
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);
  
  return (
    <button 
      onClick={onClick}
      className="relative p-2 hover:bg-gray-100 rounded-full transition-colors"
      data-testid="alert-bell-btn"
    >
      <Bell className="w-5 h-5 text-gray-600" />
      <AlertBadge count={count} />
    </button>
  );
};

// Single Alert Card
const AlertCard = ({ alert, onDismiss }) => {
  const priorityConfig = {
    high: { 
      bg: 'bg-red-50', 
      border: 'border-red-200', 
      icon: 'text-red-500',
      badge: 'bg-red-100 text-red-700'
    },
    medium: { 
      bg: 'bg-amber-50', 
      border: 'border-amber-200', 
      icon: 'text-amber-500',
      badge: 'bg-amber-100 text-amber-700'
    },
    low: { 
      bg: 'bg-blue-50', 
      border: 'border-blue-200', 
      icon: 'text-blue-500',
      badge: 'bg-blue-100 text-blue-700'
    }
  };
  
  const config = priorityConfig[alert.priority] || priorityConfig.medium;
  
  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4 relative`} data-testid={`alert-${alert.id}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-full ${config.bg}`}>
          {alert.alert_type === 'expiration' ? (
            <Calendar className={`w-5 h-5 ${config.icon}`} />
          ) : (
            <AlertTriangle className={`w-5 h-5 ${config.icon}`} />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-sm">{alert.title}</h4>
            <span className={`text-xs px-2 py-0.5 rounded-full ${config.badge}`}>
              {alert.days_remaining < 0 ? 'Vencido' : 
               alert.days_remaining <= 7 ? 'Urgente' : 
               alert.days_remaining <= 15 ? 'Próximo' : 'Aviso'}
            </span>
          </div>
          <p className="text-sm text-gray-600">{alert.message}</p>
          {alert.due_date && (
            <p className="text-xs text-gray-500 mt-1">
              Fecha: {alert.due_date}
            </p>
          )}
          <Link 
            to={`/analysis/${alert.contract_id}`}
            className="text-xs font-medium mt-2 inline-flex items-center gap-1 hover:underline"
            style={{ color: '#1A4D2E' }}
          >
            Ver contrato <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// Alerts Panel Component (for dashboard sidebar or modal)
export const AlertsPanel = ({ onClose }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAlerts();
  }, []);
  
  const fetchAlerts = async () => {
    try {
      const data = await api.getAlerts();
      setAlerts(data.alerts || []);
    } catch (e) {
      console.error('Error fetching alerts');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="bg-white border border-border shadow-lg rounded-lg w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
      <div className="p-4 border-b flex items-center justify-between" style={{ backgroundColor: '#1A4D2E' }}>
        <div className="flex items-center gap-2 text-white">
          <Bell className="w-5 h-5" />
          <h3 className="font-semibold">Alertas</h3>
          {alerts.length > 0 && (
            <span className="bg-white text-xs px-2 py-0.5 rounded-full" style={{ color: '#1A4D2E' }}>
              {alerts.length}
            </span>
          )}
        </div>
        {onClose && (
          <button onClick={onClose} className="text-white hover:bg-white/20 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        )}
      </div>
      
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: '#1A4D2E' }}></div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="text-center py-8">
            <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-gray-500 text-sm">No tienes alertas pendientes</p>
            <p className="text-gray-400 text-xs mt-1">
              Las alertas aparecerán cuando tus contratos estén por vencer
            </p>
          </div>
        ) : (
          alerts.map((alert) => (
            <AlertCard key={alert.id} alert={alert} />
          ))
        )}
      </div>
      
      {alerts.length > 0 && (
        <div className="p-3 border-t text-center">
          <p className="text-xs text-gray-500">
            Configura fechas de vencimiento en tus contratos para recibir alertas
          </p>
        </div>
      )}
    </div>
  );
};

// Alerts Summary for Dashboard
export const AlertsSummary = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const data = await api.getAlerts();
        setAlerts(data.alerts || []);
      } catch (e) {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, []);
  
  if (loading) return null;
  if (alerts.length === 0) return null;
  
  const displayAlerts = showAll ? alerts : alerts.slice(0, 3);
  
  return (
    <div className="bg-white border border-border shadow-sm rounded-lg overflow-hidden mb-6">
      <div className="p-4 border-b flex items-center justify-between" style={{ backgroundColor: '#FEF3C7' }}>
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600" />
          <h3 className="font-semibold text-amber-800">Alertas Pendientes</h3>
          <span className="bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full">
            {alerts.length}
          </span>
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        {displayAlerts.map((alert) => (
          <AlertCard key={alert.id} alert={alert} />
        ))}
        
        {alerts.length > 3 && (
          <Button 
            variant="ghost" 
            className="w-full"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Mostrar menos' : `Ver ${alerts.length - 3} más`}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AlertsPanel;
